import React, { useState } from 'react';
import { Badge, Button } from '../ui';
import { AlertTriangle, Activity, Filter } from 'lucide-react';
export function TechnicalConstraintsView() {
  const [bankTimeout, setBankTimeout] = useState(9.0);
  const [orchestratorLimit, setOrchestratorLimit] = useState(2.5);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  // Mock data for the bar chart
  const latencyData = [40, 65, 35, 20, 85, 45, 30, 25, 40, 55, 35, 20];
  return <div className="space-y-8" data-id="element-588">
      {/* Header Section */}
      <div className="flex justify-between items-start" data-id="element-589">
        <div data-id="element-590">
          <h2 className="text-3xl font-serif font-bold text-bank-navy" data-id="element-591">
            Technical Constraints & Watchdogs
          </h2>
          <p className="text-lg text-slate-500 mt-2" data-id="element-592">
            System timeouts, performance boundaries, and operational limits.
          </p>
        </div>
        <Button variant="primary" className="bg-bank-navy hover:bg-bank-navy-light" data-id="element-593">
          Apply Configuration
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-594">
        {/* LEFT COLUMN - Timer Boundaries */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-595">
          <div className="mb-8" data-id="element-596">
            <h3 className="text-xl font-serif font-bold text-bank-navy" data-id="element-597">
              Timer Boundaries (Watchdog)
            </h3>
            <p className="text-slate-500 mt-1" data-id="element-598">
              Configure maximum allowable response times before diverting to
              failure flow.
            </p>
          </div>

          <div className="space-y-8" data-id="element-599">
            {/* Slider 1 */}
            <div data-id="element-600">
              <div className="flex justify-between items-center mb-2" data-id="element-601">
                <label className="font-medium text-slate-900" data-id="element-602">
                  Bank Service Response Timeout
                </label>
                <span className="font-bold text-bank-navy text-lg" data-id="element-603">
                  {bankTimeout.toFixed(1)}s
                </span>
              </div>
              <input type="range" min="1" max="30" step="0.5" value={bankTimeout} onChange={e => setBankTimeout(parseFloat(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-bank-navy" data-id="element-604" />
              <p className="text-xs text-slate-500 mt-2" data-id="element-605">
                Maximum time allowed for a bank service to respond before
                diverting to failure flow.
                <br data-id="element-606" />
                Recommended: 9 seconds for real-time payments.
              </p>
            </div>

            {/* Slider 2 */}
            <div data-id="element-607">
              <div className="flex justify-between items-center mb-2" data-id="element-608">
                <label className="font-medium text-slate-900" data-id="element-609">
                  Orchestrator Processing Limit
                </label>
                <span className="font-bold text-bank-navy text-lg" data-id="element-610">
                  {orchestratorLimit.toFixed(1)}s
                </span>
              </div>
              <input type="range" min="0.5" max="10" step="0.5" value={orchestratorLimit} onChange={e => setOrchestratorLimit(parseFloat(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-bank-navy" data-id="element-611" />
              <p className="text-xs text-slate-500 mt-2" data-id="element-612">
                Internal processing timeout for orchestration layer.
              </p>
            </div>

            {/* Timeout Actions Box */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-5" data-id="element-613">
              <h4 className="font-bold text-bank-navy mb-4" data-id="element-614">Timeout Actions</h4>
              <div className="space-y-3" data-id="element-615">
                <div className="flex justify-between items-center" data-id="element-616">
                  <span className="text-sm text-slate-700" data-id="element-617">
                    On Bank Timeout:
                  </span>
                  <select className="bg-white border border-slate-300 text-slate-900 text-sm rounded-md focus:ring-bank-navy focus:border-bank-navy block p-1.5" data-id="element-618">
                    <option data-id="element-619">Reverse Transaction</option>
                    <option data-id="element-620">Retry</option>
                    <option data-id="element-621">Fail Immediately</option>
                  </select>
                </div>
                <div className="flex justify-between items-center" data-id="element-622">
                  <span className="text-sm text-slate-700" data-id="element-623">
                    Retry Attempts:
                  </span>
                  <input type="number" defaultValue="2" className="w-16 bg-white border border-slate-300 text-slate-900 text-sm rounded-md focus:ring-bank-navy focus:border-bank-navy block p-1.5 text-right font-mono" data-id="element-624" />
                </div>
              </div>
            </div>

            {/* Warning Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3" data-id="element-625">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-626" />
              <p className="text-sm text-amber-800" data-id="element-627">
                <span className="font-bold text-amber-900" data-id="element-628">Warning:</span>{' '}
                <span className="italic" data-id="element-629">
                  Reducing timeouts below 5s may cause increased reversal rates
                  for participants with legacy infrastructure.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Daily Posting Watchdog */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col h-full" data-id="element-630">
          <div className="mb-6" data-id="element-631">
            <h3 className="text-xl font-serif font-bold text-bank-navy" data-id="element-632">
              Daily Posting Watchdog
            </h3>
            <p className="text-slate-500 mt-1" data-id="element-633">
              Monitor ledger posting latency and status.
            </p>
          </div>

          {/* Ledger Posting Status */}
          <div className="border border-slate-200 rounded-lg p-4 flex justify-between items-center mb-8 bg-white" data-id="element-634">
            <div className="flex items-center space-x-3" data-id="element-635">
              <Activity className="h-5 w-5 text-green-600" data-id="element-636" />
              <div data-id="element-637">
                <p className="font-bold text-bank-navy text-sm" data-id="element-638">
                  Ledger Posting Status
                </p>
                <p className="text-xs text-slate-500" data-id="element-639">Real-time sync active</p>
              </div>
            </div>
            <Badge variant="success" data-id="element-640">Healthy</Badge>
          </div>

          {/* Recent Posting Latency Chart */}
          <div className="mb-8" data-id="element-641">
            <h4 className="font-bold text-bank-navy mb-4 text-sm" data-id="element-642">
              Recent Posting Latency
            </h4>
            <div className="bg-slate-50/50 rounded-lg p-4 border border-slate-100" data-id="element-643">
              <div className="h-32 flex items-end justify-between space-x-1" data-id="element-644">
                {latencyData.map((height, i) => <div key={i} className="w-full bg-slate-500 rounded-t-sm hover:bg-bank-navy transition-colors" style={{
                height: `${height}%`,
                opacity: 0.8
              }} data-id="element-645" />)}
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-400 font-mono" data-id="element-646">
                <span data-id="element-647">10:00 AM</span>
                <span data-id="element-648">10:30 AM</span>
              </div>
            </div>
          </div>

          {/* Posting Status Filter */}
          <div className="mb-8" data-id="element-649">
            <div className="flex items-center space-x-2 mb-3" data-id="element-650">
              <Filter className="h-4 w-4 text-slate-400" data-id="element-651" />
              <h4 className="font-bold text-bank-navy text-sm" data-id="element-652">
                Posting Status Filter
              </h4>
            </div>

            <div className="border border-slate-200 rounded-lg p-3 flex justify-between items-center bg-white" data-id="element-653">
              <span className="text-sm text-slate-700" data-id="element-654">
                Show Pending Entries Only
              </span>
              <button onClick={() => setShowPendingOnly(!showPendingOnly)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-bank-navy focus:ring-offset-2 ${showPendingOnly ? 'bg-green-500' : 'bg-slate-300'}`} data-id="element-655">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showPendingOnly ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-656" />
              </button>
            </div>
            <p className="text-xs text-slate-400 italic mt-2" data-id="element-657">
              Displaying all entries (Posted = True and False)
            </p>
          </div>

          {/* Recent Posting Log */}
          <div className="flex-1" data-id="element-658">
            <h4 className="font-bold text-bank-navy mb-3 text-sm" data-id="element-659">
              Recent Posting Log
            </h4>
            <div className="space-y-0" data-id="element-660">
              <div className="flex justify-between items-center py-3 border-b border-slate-100" data-id="element-661">
                <span className="font-mono text-xs text-slate-600" data-id="element-662">
                  TXN-88293-001
                </span>
                <Badge variant="success" className="text-xs py-0" data-id="element-663">
                  Posted: True
                </Badge>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-slate-100" data-id="element-664">
                <span className="font-mono text-xs text-slate-600" data-id="element-665">
                  TXN-88293-002
                </span>
                <Badge variant="success" className="text-xs py-0" data-id="element-666">
                  Posted: True
                </Badge>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-slate-100 bg-amber-50/50 -mx-2 px-2 rounded" data-id="element-667">
                <span className="font-mono text-xs text-slate-600" data-id="element-668">
                  TXN-88293-003
                </span>
                <Badge variant="warning" className="text-xs py-0 bg-amber-100 text-amber-800 border-amber-200" data-id="element-669">
                  Posted: False
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}